import React from 'react';
import PropTypes from 'prop-types';
import t from './tokens';

// Post-build retouching upsell (Shoob v2 Figma "Make their picture perfect").
// Lists the retouching add-on variants (Basic / Advanced) sourced from the Shop
// `addon` catalog; selecting one attaches the bridged Extra to the just-built
// cart line. Optional — "No thanks" continues to the cart.
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const s = {
  page: { background: t.pageBg, minHeight: '100vh', padding: '2.5rem 1.25rem' },
  inner: { maxWidth: 760, margin: '0 auto', textAlign: 'center' },
  h1: {
    fontSize: 'clamp(1.5rem,3vw,2rem)', fontWeight: 700, color: t.ink, margin: '0 0 0.5rem',
  },
  sub: { color: t.muted, margin: '0 0 2rem', fontSize: '1rem' },
  cards: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '1rem', textAlign: 'left',
  },
  card: {
    background: t.surface,
    border: '1px solid rgba(0,0,0,.08)',
    borderRadius: 14,
    boxShadow: t.shadowSm,
    padding: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
  },
  name: { fontWeight: 700, fontSize: '1.1rem', color: t.ink },
  badge: {
    marginLeft: 8,
    fontSize: '0.7rem',
    fontWeight: 700,
    color: t.badgeText,
    background: t.badgeSurface,
    borderRadius: t.radiusSm,
    padding: '0.1rem 0.4rem',
  },
  price: {
    fontSize: '1.75rem', fontWeight: 700, color: t.brand, margin: '0.5rem 0',
  },
  desc: {
    color: t.body, fontSize: '0.9rem', margin: '0 0 1rem', flex: '1 1 auto',
  },
  select: {
    background: t.brand,
    color: '#fff',
    border: 'none',
    borderRadius: t.radius,
    padding: '0.7rem',
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: '0.95rem',
  },
  skip: {
    marginTop: '1.75rem',
    background: 'none',
    border: 'none',
    color: t.muted,
    cursor: 'pointer',
    fontWeight: 600,
    textDecoration: 'underline',
    fontSize: '0.95rem',
  },
};

const RetouchingUpsell = ({
  addons, studentName, adding, onSelect, onSkip,
}) => {
  const variants = addons.flatMap((a) => a.variants.map((v) => ({ ...v, productDesc: a.description })));
  const heading = studentName ? `Make ${studentName}'s photos picture-perfect` : 'Make your photos picture-perfect';

  return (
    <div style={s.page}>
      <div style={s.inner}>
        <h1 style={s.h1}>{heading}</h1>
        <p style={s.sub}>Optional professional retouching — smooth skin, remove blemishes, and more.</p>

        <div style={s.cards}>
          {variants.map((v) => (
            <div key={v.id} style={s.card}>
              <div>
                <span style={s.name}>{v.name}</span>
                {v.badge && <span style={s.badge}>{v.badge}</span>}
              </div>
              <div style={s.price}>{money(v.price)}</div>
              <p style={s.desc}>{v.productDesc || 'Professional photo enhancement applied to your prints.'}</p>
              <button type="button" style={s.select} disabled={adding} onClick={() => onSelect(v)}>
                {`Add ${v.name}`}
              </button>
            </div>
          ))}
        </div>

        <button type="button" style={s.skip} disabled={adding} onClick={onSkip}>No thanks, continue to cart</button>
      </div>
    </div>
  );
};

RetouchingUpsell.propTypes = {
  addons: PropTypes.arrayOf(PropTypes.object).isRequired,
  studentName: PropTypes.string,
  adding: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
};

RetouchingUpsell.defaultProps = { studentName: null, adding: false };

export default RetouchingUpsell;
